/**
 * 酒店占位组件（HotelPlaceholder）
 *
 * 在门店数据尚未加载完成时展示：标题 + 加载中/空状态。
 * 数据加载后由父级替换为 StoreDataLoader，本组件不再渲染。
 */
import React from 'react';
import { Spin, Empty } from 'antd';
import { ContainerPanel } from '@ms/shared-components';
import type { ButtonConfig } from '@ms/shared-components/containers/ContainerPanel';
import { ReloadOutlined } from '@ant-design/icons';

interface HotelPlaceholderProps {
  hotelId: number;
  hotelName: string;
  loading?: boolean;
  onTitleClick?: () => void;
  onRefresh?: () => void;
  onLoad?: () => void; // 手动加载数据
}

/**
 * 酒店占位组件：显示酒店名称，内容为空时显示占位符
 */
export const HotelPlaceholder: React.FC<HotelPlaceholderProps> = ({
  hotelId,
  hotelName,
  loading = false,
  onTitleClick,
  onRefresh,
  onLoad,
}) => {
  // 占位组件挂载时触发一次加载，兜底防止批量懒加载逻辑异常导致门店永远不加载
  React.useEffect(() => {
    if (onLoad) {
      onLoad();
    }
    // 只在首次挂载时触发
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const buttons: ButtonConfig[] = [];
  
  // 移除加载数据按钮，因为使用自动加载
  // 如果有刷新函数，添加刷新按钮
  if (onRefresh) {
    buttons.push({
      key: 'refresh',
      icon: <ReloadOutlined />,
      text: '刷新',
      onClick: onRefresh,
    });
  }

  // 构建标题：hotelId - 酒店名称（名称为空时显示「门店{id」），包含加载动画
  const displayName = hotelName?.trim() || `门店${hotelId}`;
  const titleWithLoading = (
    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span>{hotelId} - {displayName}</span>
      {loading && <Spin size="small" />}
    </span>
  );

  return (
    <ContainerPanel
      key={hotelId}
      title={titleWithLoading}
      onTitleClick={onTitleClick}
      buttons={buttons}
    >
      <div
        style={{
          width: '100%',
          height: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {loading ? (
          <Spin size="large" tip="加载中..." />
        ) : (
          <Empty
            description="数据加载中，请稍候..."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </div>
    </ContainerPanel>
  );
};
